import { useEffect, useRef, useState, useCallback } from 'react';
import { MarketingMapExport } from '@/components/MarketingMapExport';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useSearchParams } from 'react-router-dom';
import {
  Amenity, CATEGORY_CONFIG,
  buildListingUrl, parseListingFromParams, DEFAULT_LISTING,
  geocodeAddress, haversineDistanceMiles, estimatedRoadMiles,
  calculateShowingCost, IRS_MILEAGE_RATE_2025,
  ShowingCalcResult,
  fetchNearbyAmenities, NearbyResult, getCategoryConfig,
  STATIC_AMENITIES,
} from '@/lib/index';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Navigation, ChevronDown, ChevronUp, List, Printer, Phone,
  Loader, Share2, Code2, Copy, Check, Calculator, MapPin, ExternalLink,
  DollarSign, Clock, Car, HomeIcon, Sparkles, Download, RefreshCw, Plus,
} from 'lucide-react';

// ─── Brand ────────────────────────────────────────────────────────────────
const BRAND = {
  red: '#CC0000',
  navy: '#003087',
  gold: '#B8860B',
  darkNavy: '#002060',
};

// ─── Leaflet icon fix ─────────────────────────────────────────────────────
delete (L.Icon.Default.prototype as unknown as Record<string, unknown>)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

function createCustomIcon(color: string, isListing = false) {
  const size = isListing ? 44 : 32;
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size + 10}" viewBox="0 0 ${size} ${size + 10}">
      <defs>
        <filter id="ds${color.replace(/[^a-z0-9]/gi, '')}" x="-30%" y="-30%" width="160%" height="160%">
          <feDropShadow dx="0" dy="2" stdDeviation="2.5" flood-color="rgba(0,0,0,0.4)"/>
        </filter>
        ${isListing ? `<linearGradient id="lg" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" style="stop-color:${BRAND.gold};stop-opacity:1"/>
          <stop offset="100%" style="stop-color:#8B6914;stop-opacity:1"/>
        </linearGradient>` : ''}
      </defs>
      <circle cx="${size / 2}" cy="${size / 2}" r="${size / 2 - 2}"
        fill="${isListing ? 'url(#lg)' : color}"
        filter="url(#ds${color.replace(/[^a-z0-9]/gi, '')})"
        stroke="white" stroke-width="2.5"/>
      ${isListing
        ? `<text x="${size / 2}" y="${size / 2 + 6}" text-anchor="middle" font-size="18" fill="white">🏠</text>`
        : `<circle cx="${size / 2}" cy="${size / 2}" r="${size * 0.22}" fill="white" opacity="0.9"/>`}
      <line x1="${size / 2}" y1="${size - 2}" x2="${size / 2}" y2="${size + 9}"
        stroke="${isListing ? BRAND.gold : color}" stroke-width="3" stroke-linecap="round"/>
    </svg>`;
  return L.divIcon({
    html: svg, className: '',
    iconSize: [size, size + 10],
    iconAnchor: [size / 2, size + 10],
    popupAnchor: [0, -(size + 10)],
  });
}

// ─── Share / Embed Modal ──────────────────────────────────────────────────
// ─── Address Autocomplete Types ───────────────────────────────────────────
interface PhotonFeature {
  properties: {
    name?: string;
    housenumber?: string;
    street?: string;
    city?: string;
    state?: string;
    postcode?: string;
    country?: string;
    type?: string;
  };
  geometry: { coordinates: [number, number] };
}

function formatPhotonAddress(f: PhotonFeature): string {
  const p = f.properties;
  const parts: string[] = [];
  if (p.housenumber && p.street) parts.push(`${p.housenumber} ${p.street}`);
  else if (p.street) parts.push(p.street);
  else if (p.name) parts.push(p.name);
  if (p.city) parts.push(p.city);
  if (p.state) parts.push(p.state);
  if (p.postcode) parts.push(p.postcode);
  return parts.join(', ');
}

function formatPhotonLabel(f: PhotonFeature): string {
  const p = f.properties;
  const line1 = [p.housenumber, p.street].filter(Boolean).join(' ') || p.name || '';
  const line2 = [p.city, p.state, p.postcode].filter(Boolean).join(', ');
  return line1 + (line2 ? ' · ' + line2 : '');
}

// ─── Create Listing Modal ─────────────────────────────────────────────────
function CreateListingModal({ onClose, onCreated, forceOpen = false }: { onClose: () => void; onCreated: (url: string) => void; forceOpen?: boolean }) {
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [confirmedCoords, setConfirmedCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [suggestions, setSuggestions] = useState<PhotonFeature[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Fetch autocomplete suggestions from Photon (OSM-based, free, no API key)
  const fetchSuggestions = useCallback(async (query: string) => {
    if (query.length < 4) { setSuggestions([]); return; }
    setSuggestLoading(true);
    try {
      // Bias toward Charlotte, NC area (lat=35.2, lng=-80.8)
      const url = `https://photon.komoot.io/api/?q=${encodeURIComponent(query)}&limit=6&lang=en&lat=35.2&lon=-80.8`;
      const res = await fetch(url, { signal: AbortSignal.timeout(5000) });
      if (!res.ok) return;
      const data = await res.json();
      // Filter to actual addresses (houses, streets) — skip parks/regions
      const filtered: PhotonFeature[] = (data.features ?? []).filter((f: PhotonFeature) =>
        ['house', 'street', 'locality', 'city'].includes(f.properties.type ?? '')
        && f.properties.country?.includes('United States')
      );
      setSuggestions(filtered.length ? filtered : data.features?.slice(0, 5) ?? []);
      setShowSuggestions(true);
    } catch {
      setSuggestions([]);
    } finally {
      setSuggestLoading(false);
    }
  }, []);

  const handleAddressChange = (val: string) => {
    setAddress(val);
    setConfirmedCoords(null); // reset confirmation when user edits
    setError('');
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(val), 350);
  };

  const selectSuggestion = (f: PhotonFeature) => {
    const [lng, lat] = f.geometry.coordinates;
    const formattedAddr = formatPhotonAddress(f);
    setAddress(formattedAddr);
    setConfirmedCoords({ lat, lng });
    setSuggestions([]);
    setShowSuggestions(false);
    setError('');
    // Auto-fill name from street address if name is empty
    if (!name.trim()) {
      const p = f.properties;
      const autoName = [p.housenumber, p.street].filter(Boolean).join(' ') || p.name || formattedAddr.split(',')[0];
      setName(autoName);
    }
  };

  const handleCreate = async () => {
    if (!name.trim() || !address.trim()) { setError('Please fill in both fields.'); return; }
    setLoading(true);
    setError('');
    // Use confirmed coords from autocomplete, or geocode as fallback
    let coords = confirmedCoords;
    if (!coords) {
      coords = await geocodeAddress(address);
    }
    if (!coords) {
      setError('Address not found. Select from the suggestions or try adding city and state.');
      setLoading(false);
      return;
    }
    const url = buildListingUrl({ id: 'listing', name: name.trim(), address: address.trim(), category: 'listing', lat: coords.lat, lng: coords.lng });
    setDone(url);
    setLoading(false);
    onCreated(url);
  };

  const copyUrl = async () => {
    const text = done!;
    try {
      if (navigator.clipboard && window.isSecureContext) await navigator.clipboard.writeText(text);
      else { const el = document.createElement('textarea'); el.value = text; el.style.cssText = 'position:fixed;top:-9999px;opacity:0'; document.body.appendChild(el); el.select(); document.execCommand('copy'); document.body.removeChild(el); }
      setCopied(true); setTimeout(() => setCopied(false), 2500);
    } catch { window.prompt('Copy this link:', text); }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      style={{ background: forceOpen ? `linear-gradient(135deg, ${BRAND.darkNavy} 0%, #001240 100%)` : 'rgba(0,0,0,0.6)' }}
      onClick={forceOpen ? undefined : onClose}
    >
      {forceOpen && (
        <div className="absolute top-8 left-0 right-0 flex flex-col items-center gap-3 px-4">
          <img src="/images/danielle-logo-white.png" alt="Danielle Edwards" className="h-14 object-contain" />
          <p className="text-white/50 text-xs">Neighborhood Map Tool</p>
        </div>
      )}
      <motion.div
        initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 40 }}
        className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 flex items-center justify-between" style={{ background: BRAND.darkNavy }}>
          <div>
            <p className="text-white font-bold">{forceOpen ? '🏠 Enter Your Listing' : '✨ New Listing Map'}</p>
            <p className="text-white/60 text-xs mt-0.5">{forceOpen ? 'Start typing — addresses will appear automatically' : 'Create a personalized map in seconds'}</p>
          </div>
          {!forceOpen && (
            <button onClick={onClose} className="text-white/50 hover:text-white"><X className="w-5 h-5" /></button>
          )}
        </div>

        <div className="p-5 space-y-4">
          {!done ? (
            <>
              {/* Address field — with autocomplete */}
              <div>
                <label className="block text-xs font-semibold mb-1.5" style={{ color: BRAND.darkNavy }}>
                  Property Address
                </label>
                <div className="relative">
                  <div className="relative">
                    <input
                      ref={inputRef}
                      type="text"
                      placeholder="Start typing: 10844 Providence Rd…"
                      value={address}
                      autoComplete="off"
                      onChange={e => handleAddressChange(e.target.value)}
                      onFocus={() => suggestions.length && setShowSuggestions(true)}
                      onBlur={() => setTimeout(() => setShowSuggestions(false), 180)}
                      onKeyDown={e => {
                        if (e.key === 'Escape') setShowSuggestions(false);
                        if (e.key === 'Enter' && !showSuggestions) handleCreate();
                      }}
                      className="w-full border rounded-xl px-4 py-3 pr-10 text-sm outline-none focus:ring-2 transition-all"
                      style={{
                        borderColor: confirmedCoords ? '#2E7D32' : showSuggestions ? BRAND.gold : '#e2d9cc',
                        '--tw-ring-color': BRAND.gold,
                      } as React.CSSProperties}
                    />
                    {/* Status icon */}
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                      {suggestLoading
                        ? <Loader className="w-4 h-4 animate-spin text-gray-400" />
                        : confirmedCoords
                          ? <Check className="w-4 h-4" style={{ color: '#2E7D32' }} />
                          : <MapPin className="w-4 h-4 text-gray-300" />
                      }
                    </div>
                  </div>

                  {/* Dropdown suggestions */}
                  {showSuggestions && suggestions.length > 0 && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-xl shadow-2xl border overflow-hidden z-50"
                      style={{ borderColor: '#e2d9cc' }}>
                      {suggestions.map((f, i) => (
                        <button
                          key={i}
                          onMouseDown={() => selectSuggestion(f)}
                          className="w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-amber-50 transition-colors border-b last:border-0"
                          style={{ borderColor: '#f5f0e8' }}
                        >
                          <MapPin className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: BRAND.gold }} />
                          <div className="min-w-0">
                            <p className="text-xs font-semibold text-gray-800 truncate">{formatPhotonLabel(f)}</p>
                            <p className="text-xs text-gray-400 truncate">{f.properties.country}</p>
                          </div>
                          <Check className="w-3 h-3 flex-shrink-0 mt-1 opacity-0 group-hover:opacity-100" style={{ color: BRAND.gold }} />
                        </button>
                      ))}
                      <div className="px-4 py-1.5 bg-gray-50 border-t" style={{ borderColor: '#e2d9cc' }}>
                        <p className="text-xs text-gray-400">📍 Powered by OpenStreetMap · Select to confirm location</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Confirmed badge */}
                {confirmedCoords && (
                  <p className="text-xs mt-1.5 font-semibold flex items-center gap-1" style={{ color: '#2E7D32' }}>
                    <Check className="w-3 h-3" /> Address verified and located on map
                  </p>
                )}
                {!confirmedCoords && address.length >= 4 && !suggestLoading && (
                  <p className="text-xs mt-1.5 text-gray-400">Select a suggestion to confirm the exact location</p>
                )}
              </div>

              {/* Property Name */}
              <div>
                <label className="block text-xs font-semibold mb-1.5" style={{ color: BRAND.darkNavy }}>
                  Property / Listing Name
                </label>
                <input
                  type="text"
                  placeholder="e.g. Promenade on Providence"
                  value={name}
                  onChange={e => { setName(e.target.value); setError(''); }}
                  className="w-full border rounded-xl px-4 py-3 text-sm outline-none focus:ring-2"
                  style={{ borderColor: '#e2d9cc', '--tw-ring-color': BRAND.gold } as React.CSSProperties}
                />
                <p className="text-xs text-gray-400 mt-1">Community name, street name, or MLS nickname</p>
              </div>

              {error && <p className="text-sm font-semibold" style={{ color: BRAND.red }}>⚠️ {error}</p>}

              <button
                onClick={handleCreate}
                disabled={loading || !name.trim() || !address.trim()}
                className="w-full py-3.5 rounded-xl text-white font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-50 transition-all"
                style={{ background: confirmedCoords ? '#1B5E20' : BRAND.darkNavy }}
              >
                {loading
                  ? <><Loader className="w-4 h-4 animate-spin" /> Locating…</>
                  : confirmedCoords
                    ? <><Check className="w-4 h-4" /> Generate Map — Address Confirmed</>
                    : <><MapPin className="w-4 h-4" /> Generate Map</>
                }
              </button>
              <p className="text-xs text-center text-gray-400">Danielle's brand & all nearby amenities load automatically.</p>
            </>
          ) : (
            <>
              <div className="text-center py-2">
                <div className="text-4xl mb-2">🎉</div>
                <p className="font-bold text-lg" style={{ color: BRAND.darkNavy }}>Map Created!</p>
                <p className="text-xs text-gray-500 mt-1">Share this link with your buyers</p>
              </div>
              <div className="rounded-xl p-3 bg-gray-50 border" style={{ borderColor: '#e2d9cc' }}>
                <p className="text-xs font-mono text-gray-600 break-all leading-relaxed">{done}</p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={copyUrl}
                  className="flex-1 py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 text-white transition-all"
                  style={{ background: copied ? '#2E7D32' : BRAND.darkNavy }}
                >
                  {copied ? <><Check className="w-4 h-4" /> Copied!</> : <><Copy className="w-4 h-4" /> Copy Link</>}
                </button>
                <a
                  href={done}
                  target="_blank"
                  rel="noreferrer"
                  className="flex-1 py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 border transition-all"
                  style={{ borderColor: BRAND.darkNavy, color: BRAND.darkNavy }}
                >
                  <ExternalLink className="w-4 h-4" /> Open Map
                </a>
              </div>
              <button onClick={() => { setDone(null); setName(''); setAddress(''); setConfirmedCoords(null); }} className="w-full text-xs text-gray-400 hover:text-gray-600 py-1">
                + Create another listing
              </button>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
}


function ShareModal({ listing, onClose }: { listing: Amenity; onClose: () => void }) {
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [copiedEmbed, setCopiedEmbed] = useState(false);
  const shareUrl = buildListingUrl(listing);
  const embedUrl = shareUrl + (shareUrl.includes('?') ? '&' : '?') + 'map=1';
  const embedCode = `<iframe\n  src="${embedUrl}"\n  width="100%"\n  height="480"\n  frameborder="0"\n  style="border-radius:10px;border:1px solid #ddd;"\n  title="${listing.name} — Locally Targeted"\n  allowfullscreen\n></iframe>`;

  // Robust copy: try modern API first, fall back to execCommand for HTTP/iframe
  const copy = async (text: string, setter: (v: boolean) => void) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback for HTTP / embedded iframes
        const el = document.createElement('textarea');
        el.value = text;
        el.style.cssText = 'position:fixed;top:-9999px;left:-9999px;opacity:0';
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
      }
      setter(true);
      setTimeout(() => setter(false), 2500);
    } catch {
      // Last resort: open a tiny prompt so user can manually copy
      window.prompt('Copy this link (Ctrl+C / Cmd+C):', text);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 12 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 12 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
      >
        {/* Header */}
        <div className="px-5 py-4 flex items-center justify-between border-b" style={{ borderColor: '#e2d9cc', background: BRAND.darkNavy }}>
          <div>
            <p className="text-white font-bold text-sm">Share this Map</p>
            <p className="text-white/60 text-xs font-mono mt-0.5">{listing.name}</p>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-5">
          {/* Share URL */}
          <div>
            <label className="text-xs font-semibold mb-1.5 block" style={{ color: BRAND.darkNavy }}>
              🔗 Shareable Link
            </label>
            <p className="text-xs text-gray-500 mb-2">
              Send this URL to buyers or put it in your listing description. Each property gets its own unique link.
            </p>
            <div className="flex gap-2">
              <input
                readOnly
                value={shareUrl}
                onFocus={e => e.target.select()}
                className="flex-1 text-xs font-mono border rounded-lg px-3 py-2 bg-gray-50 text-gray-700 min-w-0 truncate"
                style={{ borderColor: '#e2d9cc' }}
              />
              <button
                onClick={() => copy(shareUrl, setCopiedUrl)}
                className="flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-lg text-white transition-colors flex-shrink-0"
                style={{ background: copiedUrl ? '#2E7D32' : BRAND.darkNavy }}
              >
                {copiedUrl ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                {copiedUrl ? 'Copied!' : 'Copy'}
              </button>
            </div>
          </div>

          {/* Embed Code */}
          <div>
            <label className="text-xs font-semibold mb-1.5 block" style={{ color: BRAND.darkNavy }}>
              &lt;/&gt; Embed in Blog / Website
            </label>
            <p className="text-xs text-gray-500 mb-2">
              Paste this snippet into any WordPress post, website page, or HTML email to embed the live interactive map.
            </p>
            <div className="relative">
              <pre
                className="text-xs font-mono border rounded-lg p-3 bg-gray-50 text-gray-700 overflow-auto whitespace-pre"
                style={{ borderColor: '#e2d9cc', maxHeight: '120px' }}
              >{embedCode}</pre>
              <button
                onClick={() => copy(embedCode, setCopiedEmbed)}
                className="absolute top-2 right-2 flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-lg text-white transition-colors"
                style={{ background: copiedEmbed ? '#2E7D32' : BRAND.navy }}
              >
                {copiedEmbed ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                {copiedEmbed ? 'Copied!' : 'Copy'}
              </button>
            </div>
          </div>

          {/* How to change listing */}
          <div className="rounded-xl p-3 border" style={{ background: '#f8f5f0', borderColor: `${BRAND.gold}40` }}>
            <p className="text-xs font-bold mb-1" style={{ color: BRAND.darkNavy }}>💡 How to create a map for a different listing</p>
            <p className="text-xs text-gray-600 leading-relaxed">
              Add these URL parameters to swap any listing into this same map tool:
              <br />
              <code className="font-mono text-xs" style={{ color: BRAND.navy }}>?name=Your+Property&address=123+Main+St+Charlotte+NC&lat=35.05&lng=-80.78</code>
              <br />
              You can get lat/lng from Google Maps by right-clicking any location.
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

// ─── Showing Calculator ───────────────────────────────────────────────────
function ShowingCalculator({ listing }: { listing: Amenity }) {
  // ── Types ──────────────────────────────────────────────────────────────
  interface Stop {
    id: string;
    label: string;           // display name
    address: string;         // raw input
    lat: number | null;
    lng: number | null;
    isListing?: boolean;
    geocoding?: boolean;
    error?: string;
  }

  // ── State ──────────────────────────────────────────────────────────────
  const [stops, setStops] = useState<Stop[]>([
    { id: 'start', label: 'Your Starting Point', address: '', lat: null, lng: null },
    { id: listing.id, label: listing.name, address: listing.address, lat: listing.lat, lng: listing.lng, isListing: true },
  ]);
  const [timeAtProperty, setTimeAtProperty] = useState(30);
  const [hourlyRate, setHourlyRate] = useState(40);
  const [mileageRate, setMileageRate] = useState(IRS_MILEAGE_RATE_2025);
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [copied, setCopied] = useState(false);
  const [returnToStart, setReturnToStart] = useState(true);

  // ── Helpers ────────────────────────────────────────────────────────────
  const updateStop = (id: string, patch: Partial<Stop>) =>
    setStops((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)));

  const geocodeStop = async (id: string, address: string) => {
    if (!address.trim()) return;
    updateStop(id, { geocoding: true, error: undefined, lat: null, lng: null });
    const coords = await geocodeAddress(address);
    if (!coords) {
      updateStop(id, { geocoding: false, error: 'Address not found — add city & state' });
    } else {
      updateStop(id, { geocoding: false, lat: coords.lat, lng: coords.lng, error: undefined });
    }
  };

  const gpsForStop = (id: string) => {
    if (!navigator.geolocation) { updateStop(id, { error: 'GPS not available on this device.' }); return; }
    updateStop(id, { geocoding: true, error: undefined });
    navigator.geolocation.getCurrentPosition(
      (pos) => updateStop(id, { lat: pos.coords.latitude, lng: pos.coords.longitude, geocoding: false, error: undefined }),
      () => updateStop(id, { geocoding: false, error: 'Could not get GPS. Type address instead.' }),
    );
  };

  const addStop = () => {
    const newId = `stop_${Date.now()}`;
    setStops((prev) => {
      const last = prev[prev.length - 1];
      const idx = prev.length - 1;  // insert before the last (listing)
      const next = [...prev];
      next.splice(idx, 0, { id: newId, label: `Stop ${idx}`, address: '', lat: null, lng: null });
      return next;
    });
  };

  const removeStop = (id: string) =>
    setStops((prev) => prev.filter((s) => s.id !== id && !s.isListing && s.id !== 'start' ? s.id !== id : true).filter((s) => s.id !== id || s.id === 'start' || s.isListing));

  // ── Calculation ────────────────────────────────────────────────────────
  interface Leg {
    from: string;
    to: string;
    straightMi: number;
    roadMi: number;
    driveMins: number;
  }

  type ResolvedStop = Omit<Stop, 'lat' | 'lng'> & { lat: number; lng: number };
  const resolvedStops = stops.filter((s): s is ResolvedStop => s.lat !== null && s.lng !== null);

  const legs: Leg[] = [];
  if (resolvedStops.length >= 2) {
    for (let i = 0; i < resolvedStops.length - 1; i++) {
      const a = resolvedStops[i];
      const b = resolvedStops[i + 1];
      const straight = haversineDistanceMiles(a.lat, a.lng, b.lat, b.lng);
      const road = estimatedRoadMiles(straight);
      legs.push({ from: a.label, to: b.label, straightMi: straight, roadMi: road, driveMins: Math.round((road / 25) * 60) });
    }
    // Return leg
    if (returnToStart && resolvedStops.length >= 2) {
      const last = resolvedStops[resolvedStops.length - 1];
      const first = resolvedStops[0];
      const straight = haversineDistanceMiles(last.lat, last.lng, first.lat, first.lng);
      const road = estimatedRoadMiles(straight);
      legs.push({ from: last.label, to: first.label + ' (return)', straightMi: straight, roadMi: road, driveMins: Math.round((road / 25) * 60) });
    }
  }

  const totalRoadMi = legs.reduce((s, l) => s + l.roadMi, 0);
  const totalDriveMins = legs.reduce((s, l) => s + l.driveMins, 0);
  // Time at each listing stop (not start, not pure intermediate non-listing stops)
  const listingStops = stops.filter((s) => s.isListing).length;
  const totalTimeMins = totalDriveMins + timeAtProperty * Math.max(listingStops, 1);
  const mileageCost = totalRoadMi * mileageRate;
  const laborCost = (totalTimeMins / 60) * hourlyRate;
  const subtotal = mileageCost + laborCost;
  const roundedTotal = Math.ceil(subtotal / 5) * 5;

  const hasResult = legs.length > 0;

  const copyReceipt = async () => {
    const lines = [
      'SHOWING COST CALCULATION',
      `Property: ${listing.name}`,
      '',
      ...legs.map((l) => `  ${l.from} → ${l.to}: ${l.roadMi.toFixed(1)} mi, ${l.driveMins} min`),
      '',
      `Total miles: ${totalRoadMi.toFixed(1)} mi`,
      `Total time:  ${totalTimeMins} min`,
      '',
      `Mileage cost: $${mileageCost.toFixed(2)}`,
      `Labor cost:   $${laborCost.toFixed(2)}`,
      `Subtotal:     $${subtotal.toFixed(2)}`,
      `TOTAL (↑$5):  $${roundedTotal}.00`,
      '',
      'Powered by SoldONDanielle.com',
    ];
    const text = lines.join('\n');
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        const el = document.createElement('textarea');
        el.value = text;
        el.style.cssText = 'position:fixed;top:-9999px;left:-9999px;opacity:0';
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      window.prompt('Copy this receipt:', text);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <div className="flex-1 overflow-y-auto p-4 space-y-4">
      {/* Header */}
      <div className="rounded-xl p-3 border text-xs leading-relaxed" style={{ background: '#f8f5f0', borderColor: `${BRAND.gold}40`, color: BRAND.darkNavy }}>
        <p className="font-bold mb-1">🧮 Multi-Stop Showing Calculator</p>
        <p className="text-gray-600">Plan your full route — add as many stops as you need. Costs sum across all legs automatically.</p>
      </div>

      {/* Route builder */}
      <div className="space-y-2">
        {stops.map((stop, idx) => (
          <div key={stop.id} className="relative">
            {/* Connector line */}
            {idx < stops.length - 1 && (
              <div className="absolute left-3.5 top-full w-px h-2 z-10" style={{ background: '#e2d9cc' }} />
            )}
            <div className="rounded-xl border overflow-hidden" style={{ borderColor: stop.isListing ? BRAND.gold : '#e2d9cc', background: stop.isListing ? '#fffbf0' : 'white' }}>
              <div className="flex items-center gap-2 px-3 py-2 border-b" style={{ borderColor: '#f0e8d8', background: stop.isListing ? `${BRAND.gold}18` : '#fafafa' }}>
                <span className="text-base leading-none">{idx === 0 ? '🏢' : stop.isListing ? '🏠' : '📍'}</span>
                <span className="text-xs font-bold flex-1 truncate" style={{ color: BRAND.darkNavy }}>{stop.label || `Stop ${idx}`}</span>
                {stop.lat !== null && <span className="text-xs text-green-600 flex items-center gap-0.5"><Check className="w-3 h-3" />Set</span>}
                {!stop.isListing && stop.id !== 'start' && (
                  <button onClick={() => removeStop(stop.id)} className="text-gray-300 hover:text-red-400 transition-colors ml-1">
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>

              {!stop.isListing ? (
                <div className="p-2 space-y-1.5">
                  <div className="flex gap-1.5">
                    <input
                      type="text"
                      placeholder={idx === 0 ? 'Your office or home address…' : 'Address…'}
                      value={stop.address}
                      onChange={(e) => updateStop(stop.id, { address: e.target.value, lat: null, lng: null, error: undefined })}
                      onKeyDown={(e) => e.key === 'Enter' && geocodeStop(stop.id, stop.address)}
                      className="flex-1 text-xs border rounded-lg px-2.5 py-1.5 outline-none focus:ring-1 min-w-0"
                      style={{ borderColor: '#e2d9cc' }}
                    />
                    <button
                      onClick={() => geocodeStop(stop.id, stop.address)}
                      disabled={stop.geocoding || !stop.address.trim()}
                      className="flex items-center gap-0.5 text-xs font-bold px-2.5 py-1.5 rounded-lg text-white disabled:opacity-40 flex-shrink-0"
                      style={{ background: BRAND.darkNavy }}
                    >
                      {stop.geocoding ? <Loader className="w-3 h-3 animate-spin" /> : <MapPin className="w-3 h-3" />}
                    </button>
                    {idx === 0 && (
                      <button
                        onClick={() => gpsForStop(stop.id)}
                        disabled={stop.geocoding}
                        title="Use GPS"
                        className="flex items-center text-xs font-bold px-2 py-1.5 rounded-lg border disabled:opacity-40 flex-shrink-0"
                        style={{ borderColor: BRAND.navy, color: BRAND.navy }}
                      >
                        <Car className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                  {stop.error && <p className="text-xs" style={{ color: BRAND.red }}>{stop.error}</p>}
                </div>
              ) : (
                <div className="px-3 py-1.5">
                  <p className="text-xs font-mono text-gray-500 truncate">{stop.address}</p>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Add stop button */}
      <button
        onClick={addStop}
        className="w-full flex items-center justify-center gap-2 py-2 rounded-xl border-2 border-dashed text-xs font-bold transition-colors hover:border-solid"
        style={{ borderColor: BRAND.gold, color: BRAND.gold }}
      >
        <Plus className="w-3.5 h-3.5" />
        Add Another Stop
      </button>

      {/* Return to start toggle */}
      <label className="flex items-center gap-2 cursor-pointer text-xs font-semibold" style={{ color: BRAND.darkNavy }}>
        <div
          onClick={() => setReturnToStart((v) => !v)}
          className="w-9 h-5 rounded-full relative transition-colors flex-shrink-0"
          style={{ background: returnToStart ? BRAND.navy : '#e2d9cc' }}
        >
          <div className="absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all" style={{ left: returnToStart ? '19px' : '2px' }} />
        </div>
        Include return trip to start
      </label>

      {/* Settings */}
      <div className="space-y-3">
        <div>
          <label className="text-xs font-semibold mb-1 block" style={{ color: BRAND.darkNavy }}>
            How long will you spend at the property?
          </label>
          <p className="text-xs text-gray-400 mb-1.5">This only affects your time cost, not gas.</p>
          <div className="flex items-center gap-1.5">
            {[15, 30, 45, 60, 90].map((m) => (
              <button
                key={m}
                onClick={() => setTimeAtProperty(m)}
                className="flex-1 py-1.5 rounded-lg text-xs font-bold border transition-all"
                style={timeAtProperty === m
                  ? { background: BRAND.darkNavy, color: 'white', borderColor: BRAND.darkNavy }
                  : { background: 'transparent', color: '#666', borderColor: '#e2d9cc' }}
              >
                {m}m
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-semibold mb-1 block text-gray-600">Hourly Rate ($/hr)</label>
            <input type="number" min={0} step={5} value={hourlyRate}
              onChange={(e) => setHourlyRate(parseFloat(e.target.value) || 0)}
              className="w-full text-sm font-mono border rounded-lg px-3 py-2 outline-none"
              style={{ borderColor: '#e2d9cc' }} />
          </div>
          <div>
            <label className="text-xs font-semibold mb-1 block text-gray-600">$/Mile (IRS)</label>
            <input type="number" min={0} step={0.005} value={mileageRate}
              onChange={(e) => setMileageRate(parseFloat(e.target.value) || 0)}
              className="w-full text-sm font-mono border rounded-lg px-3 py-2 outline-none"
              style={{ borderColor: '#e2d9cc' }} />
          </div>
        </div>
      </div>

      {/* Result */}
      <AnimatePresence>
        {hasResult && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className="rounded-xl overflow-hidden border" style={{ borderColor: BRAND.gold }}>
            {/* Total */}
            <div className="px-4 py-3 text-center" style={{ background: BRAND.darkNavy }}>
              <p className="text-white/70 text-xs font-semibold mb-0.5">Total Showing Cost</p>
              <p className="text-white font-black text-4xl" style={{ fontFamily: 'inherit' }}>${roundedTotal}</p>
              <p className="text-white/50 text-xs mt-0.5">Rounded up to nearest $5</p>
            </div>
            {/* Stats */}
            <div className="grid grid-cols-3 divide-x" style={{ background: '#fafafa', borderColor: '#e2d9cc' }}>
              <div className="p-2.5 text-center">
                <Car className="w-3.5 h-3.5 mx-auto mb-0.5" style={{ color: BRAND.navy }} />
                <p className="text-xs font-bold" style={{ color: BRAND.darkNavy }}>{totalRoadMi.toFixed(1)} mi</p>
                <p className="text-xs text-gray-400">driving miles</p>
              </div>
              <div className="p-2.5 text-center">
                <Clock className="w-3.5 h-3.5 mx-auto mb-0.5" style={{ color: BRAND.navy }} />
                <p className="text-xs font-bold" style={{ color: BRAND.darkNavy }}>{totalTimeMins} min</p>
                <p className="text-xs text-gray-400">drive + showing</p>
              </div>
              <div className="p-2.5 text-center">
                <DollarSign className="w-3.5 h-3.5 mx-auto mb-0.5" style={{ color: BRAND.navy }} />
                <p className="text-xs font-bold" style={{ color: BRAND.darkNavy }}>${subtotal.toFixed(2)}</p>
                <p className="text-xs text-gray-400">before rounding</p>
              </div>
            </div>
            {/* Leg breakdown */}
            <button onClick={() => setShowBreakdown((v) => !v)}
              className="w-full flex items-center justify-between px-4 py-2 border-t text-xs font-semibold"
              style={{ borderColor: '#e2d9cc', color: BRAND.darkNavy }}>
              Route Breakdown
              {showBreakdown ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>
            <AnimatePresence>
              {showBreakdown && (
                <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }}
                  className="overflow-hidden border-t" style={{ borderColor: '#e2d9cc' }}>
                  <div className="px-4 py-3 space-y-2 bg-white text-xs">
                    {legs.map((leg, i) => (
                      <div key={i} className="flex justify-between text-gray-600 pb-1.5 border-b last:border-0" style={{ borderColor: '#f0e8d8' }}>
                        <span className="truncate flex-1 mr-2">→ {leg.from} <span className="text-gray-400">to</span> {leg.to}</span>
                        <span className="font-mono flex-shrink-0">{leg.roadMi.toFixed(1)} mi · {leg.driveMins}min</span>
                      </div>
                    ))}
                    <div className="pt-1 space-y-1">
                      <div className="flex justify-between text-gray-600">
                        <span>⛽ Gas / Mileage ({totalRoadMi.toFixed(1)} mi × ${mileageRate.toFixed(3)})</span>
                        <span className="font-mono font-semibold">${mileageCost.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-gray-600">
                        <span>🕐 Your time ({(totalTimeMins / 60).toFixed(2)} hrs × ${hourlyRate}/hr) — drive + showing</span>
                        <span className="font-mono font-semibold">${laborCost.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between pt-1 border-t font-bold" style={{ borderColor: '#e2d9cc', color: BRAND.darkNavy }}>
                        <span>Subtotal</span><span className="font-mono">${subtotal.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between font-black text-sm" style={{ color: BRAND.red }}>
                        <span>TOTAL (rounded ↑$5)</span><span className="font-mono">${roundedTotal}.00</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
            <button onClick={copyReceipt}
              className="w-full flex items-center justify-center gap-2 py-2.5 text-xs font-bold border-t transition-colors"
              style={{ borderColor: '#e2d9cc', color: copied ? '#2E7D32' : BRAND.navy }}>
              {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
              {copied ? 'Copied!' : 'Copy Receipt to Clipboard'}
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Brand Logo Lookup ────────────────────────────────────────────────────
const BRAND_LOGO_MAP: Record<string, string> = {
  'harris teeter': 'harristeeter.com',
  "lowe's home improvement": 'lowes.com',
  "lowe's foods": 'lowesfoods.com',
  "lowe's": 'lowes.com',
  'the home depot': 'homedepot.com',
  'home depot': 'homedepot.com',
  'whole foods market': 'wholefoodsmarket.com',
  'whole foods': 'wholefoodsmarket.com',
  "trader joe's": 'traderjoes.com',
  'the fresh market': 'thefreshmarket.com',
  'fresh market': 'thefreshmarket.com',
  'publix': 'publix.com',
  'kroger': 'kroger.com',
  'food lion': 'foodlion.com',
  'aldi': 'aldi.us',
  'lidl': 'lidl.com',
  'target': 'target.com',
  'walmart': 'walmart.com',
  'costco': 'costco.com',
  'sam\'s club': 'samsclub.com',
  'cvs pharmacy': 'cvs.com',
  'cvs': 'cvs.com',
  'walgreens': 'walgreens.com',
  'rite aid': 'riteaid.com',
  'chick-fil-a': 'chick-fil-a.com',
  'starbucks': 'starbucks.com',
  "mcdonald's": 'mcdonalds.com',
  'chipotle': 'chipotle.com',
  'panera bread': 'panerabread.com',
  'panera': 'panerabread.com',
  'five guys': 'fiveguys.com',
  'pizza hut': 'pizzahut.com',
  'domino\'s': 'dominos.com',
  'subway': 'subway.com',
  'dunkin': 'dunkindonuts.com',
  'bojangles': 'bojangles.com',
  'cook out': 'cookout.com',
  'cookout': 'cookout.com',
  'zaxby\'s': 'zaxbys.com',
  'lowes hardware': 'lowes.com',
  'ace hardware': 'acehardware.com',
  'true value': 'truevalue.com',
};

function getBrandLogoUrl(name: string): string | null {
  const lower = name.toLowerCase().trim();
  // Exact match first
  if (BRAND_LOGO_MAP[lower]) return `https://logo.clearbit.com/${BRAND_LOGO_MAP[lower]}`;
  // Partial match
  for (const [key, domain] of Object.entries(BRAND_LOGO_MAP)) {
    if (lower.includes(key) || key.includes(lower)) {
      return `https://logo.clearbit.com/${domain}`;
    }
  }
  return null;
}

// ─── Amenity Grouping for Infographic ────────────────────────────────────
type BoxPos = 'NW' | 'NE' | 'SW' | 'SE' | 'S' | 'E';
interface CalloutGroup {
  pos: BoxPos;
  label: string;
  items: Array<{ name: string; logoUrl: string | null; category: string }>;
}

function groupAmenitiesForInfographic(listing: Amenity, amenities: NearbyResult[]): CalloutGroup[] {
  // Separate highways and parks — they get fixed positions
  const highways = amenities.filter(a => a.category === 'highway');
  const parks    = amenities.filter(a => a.category === 'park').slice(0, 1);
  const rest     = amenities.filter(a => a.category !== 'highway' && a.category !== 'park');

  // Bucket remaining amenities by geographic direction from listing
  const nw: NearbyResult[] = [], ne: NearbyResult[] = [],
        sw: NearbyResult[] = [], se: NearbyResult[] = [];
  for (const a of rest) {
    const north = a.lat  >= listing.lat;
    const west  = a.lng  <= listing.lng;
    if (north && west)  nw.push(a);
    else if (north)     ne.push(a);
    else if (west)      sw.push(a);
    else                se.push(a);
  }

  const toItems = (arr: NearbyResult[]) =>
    arr.slice(0, 4).map(a => ({ name: a.name, logoUrl: getBrandLogoUrl(a.name), category: a.category }));

  const boxes: CalloutGroup[] = [];

  if (nw.length) boxes.push({ pos: 'NW', label: nw[0].description?.includes('mi') ? nw[0].name : 'Nearby Shops', items: toItems(nw) });
  if (ne.length) boxes.push({ pos: 'NE', label: ne[0].description?.includes('mi') ? ne[0].name : 'Shopping', items: toItems(ne) });
  if (sw.length) boxes.push({ pos: 'SW', label: sw[0].description?.includes('mi') ? sw[0].name : 'Services', items: toItems(sw) });
  if (se.length) boxes.push({ pos: 'SE', label: se[0].description?.includes('mi') ? se[0].name : 'Dining', items: toItems(se) });
  if (parks.length)    boxes.push({ pos: 'S',  label: parks[0].name, items: [] });
  if (highways.length) boxes.push({ pos: 'E',  label: highways[0].name, items: [] });

  return boxes;
}

// ─── Infographic Export Modal ─────────────────────────────────────────────
const BOX_STYLE: Record<BoxPos, React.CSSProperties> = {
  NW: { top: '6%',  left: '2%'  },
  NE: { top: '6%',  right: '2%' },
  SW: { bottom: '18%', left: '2%' },
  SE: { bottom: '18%', right: '2%' },
  S:  { bottom: '4%',  left: '50%', transform: 'translateX(-50%)' },
  E:  { top: '50%', right: '2%', transform: 'translateY(-50%)' },
};

// Arrow connector pixel coords — box center per position, toward home pin at 50%/50%

function InfographicExportModal({ listing, amenities, onClose }: {
  listing: Amenity; amenities: NearbyResult[]; onClose: () => void;
}) {
  const mapRef  = useRef<HTMLDivElement>(null);
  const printRef = useRef<HTMLDivElement>(null);
  const leafletRef = useRef<L.Map | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [logosLoaded, setLogosLoaded] = useState(false);
  const groups = groupAmenitiesForInfographic(listing, amenities);

  // Map for this infographic — CartoDB Positron (clean light style, CORS-enabled tiles)
  useEffect(() => {
    if (!mapRef.current || leafletRef.current) return;
    const map = L.map(mapRef.current, {
      zoomControl: false, attributionControl: false,
      dragging: false, scrollWheelZoom: false, doubleClickZoom: false,
    });
    L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
      subdomains: 'abcd', crossOrigin: 'anonymous',
    }).addTo(map);
    map.setView([listing.lat, listing.lng], 14);
    leafletRef.current = map;
    // Signal tiles likely loaded after 2s
    setTimeout(() => setLogosLoaded(true), 2200);
    return () => { map.remove(); leafletRef.current = null; };
  }, [listing]);

  const handleDownloadPNG = async () => {
    if (!printRef.current) return;
    setDownloading(true);
    try {
      // Dynamically import html2canvas only when needed
      const { default: html2canvas } = await import('html2canvas');
      await new Promise(r => setTimeout(r, 800)); // final tile settle
      const canvas = await html2canvas(printRef.current, {
        useCORS: true, allowTaint: false,
        scale: 2, logging: false,
        width: 900, height: 636,
        windowWidth: 900,
      });
      const url = canvas.toDataURL('image/png');
      const a = document.createElement('a');
      a.href = url;
      a.download = `${listing.name.replace(/[^a-z0-9]/gi, '-')}-minutes-to-everything.png`;
      a.click();
    } catch (e) {
      console.error('PNG export failed', e);
      alert('PNG download failed — use Print / Save PDF instead.');
    } finally {
      setDownloading(false);
    }
  };

  const handlePrint = () => window.print();

  return (
    <div className="fixed inset-0 z-[60] flex flex-col items-center justify-start overflow-auto py-4 px-2"
      style={{ background: 'rgba(0,0,0,0.85)' }}>

      {/* Controls bar */}
      <div className="no-print flex items-center gap-2 mb-3 flex-wrap justify-center">
        <button onClick={handleDownloadPNG} disabled={downloading || !logosLoaded}
          className="flex items-center gap-1.5 text-white text-sm font-bold px-4 py-2 rounded-full disabled:opacity-50 transition-all"
          style={{ background: logosLoaded ? BRAND.darkNavy : '#666' }}>
          {downloading ? <Loader className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
          {downloading ? 'Generating…' : logosLoaded ? 'Download PNG' : 'Loading map…'}
        </button>
        <button onClick={handlePrint}
          className="flex items-center gap-1.5 text-white text-sm font-bold px-4 py-2 rounded-full transition-all"
          style={{ background: BRAND.red }}>
          <Printer className="w-4 h-4" /> Print / PDF
        </button>
        <button onClick={onClose}
          className="flex items-center gap-1.5 bg-white/20 text-white text-sm font-bold px-4 py-2 rounded-full hover:bg-white/30 transition-all">
          <X className="w-4 h-4" /> Close
        </button>
      </div>

      {/* The infographic — this div gets captured by html2canvas */}
      <div ref={printRef} style={{
        width: '900px', minWidth: '900px', background: '#fff',
        border: `4px solid ${BRAND.darkNavy}`,
        borderRadius: '4px', overflow: 'hidden', fontFamily: 'Georgia, serif',
        boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
      }}>
        {/* Top gold bar */}
        <div style={{ height: '6px', background: `linear-gradient(90deg, ${BRAND.gold}, ${BRAND.red}, ${BRAND.gold})` }} />

        {/* Header — single address, no duplicate */}
        <div style={{ padding: '14px 24px 10px', textAlign: 'center', background: '#fff' }}>
          <h1 style={{ fontSize: '40px', fontWeight: '900', color: BRAND.darkNavy, letterSpacing: '-1px', lineHeight: 1, margin: 0, fontFamily: 'Georgia, serif' }}>
            LOCALLY TARGETED
          </h1>
          <p style={{ fontSize: '12px', letterSpacing: '0.14em', color: BRAND.gold, fontWeight: '700', textTransform: 'uppercase', marginTop: '5px' }}>
            {listing.name && listing.name !== listing.address ? listing.name : listing.address.split(',')[0]}
          </p>
        </div>

        {/* Map area with overlaid callout boxes */}
        <div style={{ position: 'relative', height: '460px', overflow: 'hidden' }}>

          {/* Leaflet map */}
          <div ref={mapRef} style={{ position: 'absolute', inset: 0, zIndex: 0 }} />

          {/* SVG layer — connecting lines from boxes to home pin */}
          <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', zIndex: 1, pointerEvents: 'none' }}>
            {groups.map(g => {
              // Home pin is at 50%, 50% of the map area
              const cx = 450, cy = 230; // center of 900×460 map
              // Box approximate centers based on position
              const bx: Record<BoxPos, number> = { NW: 150, NE: 760, SW: 150, SE: 760, S: 450, E: 820 };
              const by: Record<BoxPos, number> = { NW: 100, NE: 100, SW: 360, SE: 360, S: 420, E: 230 };
              return (
                <line key={g.pos}
                  x1={bx[g.pos]} y1={by[g.pos]} x2={cx} y2={cy}
                  stroke={BRAND.darkNavy} strokeWidth="2.5" strokeDasharray="none"
                  markerEnd="url(#arrow)"
                />
              );
            })}
            <defs>
              <marker id="arrow" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
                <path d="M0,0 L0,6 L8,3 z" fill={BRAND.darkNavy} />
              </marker>
            </defs>
          </svg>

          {/* Home pin at center */}
          <div style={{
            position: 'absolute', left: '50%', top: '50%',
            transform: 'translate(-50%, -100%)',
            zIndex: 10, textAlign: 'center',
          }}>
            <div style={{
              width: '52px', height: '52px', borderRadius: '50% 50% 50% 0',
              transform: 'rotate(-45deg)',
              background: BRAND.darkNavy, border: `3px solid ${BRAND.gold}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
            }}>
              <span style={{ transform: 'rotate(45deg)', fontSize: '22px' }}>🏠</span>
            </div>
            <div style={{
              marginTop: '4px',
              background: BRAND.darkNavy, color: '#fff',
              fontSize: '10px', fontWeight: '700', fontFamily: 'Arial, sans-serif',
              padding: '3px 10px', borderRadius: '4px', whiteSpace: 'nowrap',
              boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
            }}>
              {listing.address.split(',')[0]}
            </div>
          </div>

          {/* Callout boxes */}
          {groups.map(g => (
            <div key={g.pos} style={{
              position: 'absolute', zIndex: 5,
              background: 'rgba(255,255,255,0.97)',
              border: `2px solid ${BRAND.darkNavy}`,
              borderRadius: '8px',
              padding: g.pos === 'E' ? '10px 12px' : '10px 14px',
              minWidth: g.pos === 'E' ? '70px' : '180px',
              maxWidth: '230px',
              boxShadow: '0 3px 12px rgba(0,0,0,0.25)',
              ...BOX_STYLE[g.pos],
            }}>
              {/* Box title */}
              <p style={{
                fontFamily: 'Georgia, serif', fontWeight: '700',
                fontSize: g.pos === 'E' ? '11px' : '13px',
                color: BRAND.darkNavy, margin: '0 0 6px 0',
                textAlign: g.pos === 'E' ? 'center' : 'left',
                lineHeight: 1.2,
              }}>
                {g.pos === 'E' ? '🛣️' : ''} {g.label}
              </p>

              {/* Place name pills — always render, no broken images */}
              {g.items.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                  {g.items.slice(0, 3).map((item, i) => {
                    const cfg = getCategoryConfig(item.category);
                    return (
                      <span key={i} style={{
                        fontSize: '10px', fontWeight: '700', fontFamily: 'Arial, sans-serif',
                        background: cfg.color, color: '#fff',
                        padding: '3px 8px', borderRadius: '4px',
                        whiteSpace: 'nowrap', maxWidth: '120px',
                        overflow: 'hidden', textOverflow: 'ellipsis', display: 'block',
                      }}>
                        {item.name}
                      </span>
                    );
                  })}
                </div>
              )}

              {/* Park icon */}
              {g.pos === 'S' && (
                <div style={{ fontSize: '28px', textAlign: 'center' }}>🌳</div>
              )}
            </div>
          ))}
        </div>

        {/* Bottom gold bar */}
        <div style={{ height: '3px', background: `linear-gradient(90deg, ${BRAND.gold}, ${BRAND.red}, ${BRAND.gold})` }} />

        {/* Footer */}
        <div style={{
          padding: '10px 24px', display: 'flex',
          alignItems: 'center', justifyContent: 'space-between',
          background: '#fff',
        }}>
          <img src="/images/danielle-logo.png" alt="Danielle Edwards"
            style={{ height: '52px', objectFit: 'contain' }} />
          <div style={{ textAlign: 'center' }}>
            <p style={{ fontSize: '10px', color: '#999', fontFamily: 'Arial, sans-serif', margin: 0 }}>
              SoldONDanielle.com · 704-604-2999
            </p>
          </div>
          <img src="/images/remax-black.png" alt="RE/MAX Executive"
            style={{ height: '42px', objectFit: 'contain' }} />
        </div>

        {/* Bottom navy bar */}
        <div style={{ height: '6px', background: BRAND.darkNavy }} />
      </div>
    </div>
  );
}

// ─── Export Chooser Modal ─────────────────────────────────────────────────
function ExportChooserModal({ onPrintSheet, onInfographic, onMarketingMap, onClose }: {
  onPrintSheet: () => void; onInfographic: () => void; onMarketingMap: () => void; onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.6)' }}
      onClick={onClose}>
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-2xl shadow-2xl overflow-hidden w-full max-w-sm"
        onClick={e => e.stopPropagation()}>

        <div className="px-5 py-4" style={{ background: BRAND.darkNavy }}>
          <p className="text-white font-bold">Export Map</p>
          <p className="text-white/60 text-xs mt-0.5">Choose your format</p>
        </div>

        <div className="p-4 space-y-3">
          {/* Infographic option */}
          <button onClick={onInfographic}
            className="w-full flex items-start gap-3 p-3 rounded-xl border-2 text-left hover:shadow-md transition-all"
            style={{ borderColor: BRAND.darkNavy }}>
            <div className="text-2xl flex-shrink-0">🖼️</div>
            <div>
              <p className="font-bold text-sm" style={{ color: BRAND.darkNavy }}>Infographic — "Locally Targeted"</p>
              <p className="text-xs text-gray-500 mt-0.5">Map with callout boxes, brand logos & Danielle's branding. Perfect for MLS, blog & social.</p>
            </div>
          </button>

          {/* Marketing Map option — NEW, matches MAP.png / MAP 2.png */}
          <button onClick={onMarketingMap}
            className="w-full flex items-start gap-3 p-3 rounded-xl border-2 text-left hover:shadow-md transition-all"
            style={{ borderColor: BRAND.gold, background: '#FFFDF5' }}>
            <div className="text-2xl flex-shrink-0">🗺️</div>
            <div>
              <p className="font-bold text-sm" style={{ color: BRAND.darkNavy }}>Marketing Map — Locally Targeted</p>
              <p className="text-xs text-gray-500 mt-0.5">Professional PNG/JPG/PDF with callout boxes, drive-time chips, amenities panel & Danielle branding. Ready for MLS, blog & social.</p>
              <p className="text-xs font-semibold mt-1" style={{ color: BRAND.gold }}>⭐ Recommended</p>
            </div>
          </button>

          {/* Print sheet option */}
          <button onClick={onPrintSheet}
            className="w-full flex items-start gap-3 p-3 rounded-xl border text-left hover:shadow-md transition-all"
            style={{ borderColor: '#e2d9cc' }}>
            <div className="text-2xl flex-shrink-0">📄</div>
            <div>
              <p className="font-bold text-sm" style={{ color: BRAND.darkNavy }}>Print Sheet — Amenities List</p>
              <p className="text-xs text-gray-500 mt-0.5">Full amenity list with addresses. Good for printing & saving as PDF.</p>
            </div>
          </button>

          <button onClick={onClose} className="w-full text-xs text-gray-400 hover:text-gray-600 py-1">Cancel</button>
        </div>
      </motion.div>
    </div>
  );
}

// ─── Print View ───────────────────────────────────────────────────────────
function PrintView({ onClose, mapImageUrl, listing, nearbyAmenities }: { onClose: () => void; mapImageUrl: string; listing: Amenity; nearbyAmenities: NearbyResult[] }) {
  return (
    <div className="fixed inset-0 z-50 bg-white overflow-auto">
      <div className="no-print sticky top-0 z-10 bg-gray-100 border-b px-4 py-2 flex items-center justify-between">
        <span className="text-sm font-semibold text-gray-700">Print / Export Preview</span>
        <div className="flex gap-2">
          <button
            onClick={() => window.print()}
            className="flex items-center gap-1.5 bg-blue-700 text-white text-sm font-semibold px-4 py-1.5 rounded-full hover:bg-blue-800 transition-colors"
          >
            <Printer className="w-3.5 h-3.5" />
            Print / Save PDF
          </button>
          <button
            onClick={onClose}
            className="flex items-center gap-1.5 bg-gray-200 text-gray-700 text-sm font-semibold px-4 py-1.5 rounded-full hover:bg-gray-300 transition-colors"
          >
            <X className="w-3.5 h-3.5" />
            Close
          </button>
        </div>
      </div>

      <div id="print-content" style={{ width: '8.5in', minHeight: '11in', margin: '0 auto', background: 'white', fontFamily: "'Montserrat', sans-serif", padding: '0.4in 0.5in', boxSizing: 'border-box' }}>
        <div style={{ height: '8px', background: BRAND.red, borderRadius: '4px', marginBottom: '18px' }} />

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
          <img src="/images/danielle-logo.png" alt="Danielle Edwards" style={{ height: '72px', objectFit: 'contain' }} />
          <div style={{ textAlign: 'center', flex: 1, padding: '0 16px' }}>
            <div style={{ fontSize: '11px', color: '#666', letterSpacing: '0.15em', marginBottom: '2px' }}>
              {listing.address}
            </div>
            <div style={{ fontSize: '28px', fontWeight: '900', color: BRAND.darkNavy, letterSpacing: '-0.5px', lineHeight: 1.1 }}>
              LOCALLY TARGETED
            </div>
            <div style={{ fontSize: '11px', color: BRAND.red, fontWeight: '700', letterSpacing: '0.1em', marginTop: '3px' }}>
              {listing.name.toUpperCase()} · South Charlotte, NC
            </div>
          </div>
          <img src="/images/remax-black.png" alt="RE/MAX Executive" style={{ height: '56px', objectFit: 'contain' }} />
        </div>

        <div style={{ height: '2px', background: `linear-gradient(90deg, ${BRAND.gold}, ${BRAND.navy}, ${BRAND.gold})`, marginBottom: '18px' }} />

        <div style={{ width: '100%', height: '340px', borderRadius: '8px', overflow: 'hidden', border: `3px solid ${BRAND.navy}`, marginBottom: '18px', position: 'relative', background: '#e8e0d8' }}>
          {mapImageUrl
            ? mapImageUrl.includes('openstreetmap.org/export/embed')
              ? <iframe
                  src={mapImageUrl}
                  title="Neighborhood Map"
                  style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
                  loading="eager"
                />
              : <img src={mapImageUrl} alt="Neighborhood Map" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
            : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '8px' }}>
                <div style={{ fontSize: '28px' }}>🗺️</div>
                <div style={{ fontSize: '12px', color: '#888' }}>Generating map…</div>
              </div>
          }
          <div style={{ position: 'absolute', bottom: '10px', left: '50%', transform: 'translateX(-50%)', background: 'rgba(0,32,96,0.92)', borderRadius: '6px', padding: '5px 14px', whiteSpace: 'nowrap' }}>
            <span style={{ color: 'white', fontSize: '11px', fontWeight: '700', letterSpacing: '0.04em' }}>
              🏠 {listing.address}
            </span>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginBottom: '16px' }}>
          {nearbyAmenities
            .filter((a) => !a.distanceMiles || a.distanceMiles <= 6) // max 6 miles — keep only nearby
            .filter((a, i, arr) => arr.findIndex(b => b.name === a.name) === i) // deduplicate by name
            .map((amenity) => {
            const config = getCategoryConfig(amenity.category);
            return (
              <div key={amenity.id} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', padding: '8px 10px', borderRadius: '6px', border: '1px solid #e2e8f0', background: '#fafafa' }}>
                <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: config.color, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px' }}>
                  {config.emoji}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: '700', fontSize: '11px', color: BRAND.darkNavy, lineHeight: 1.2 }}>{amenity.name}</div>
                  <div style={{ fontSize: '9px', color: '#666', fontFamily: 'monospace', lineHeight: 1.3, marginTop: '2px' }}>{amenity.address}</div>
                  {amenity.description && <div style={{ fontSize: '9px', color: '#888', lineHeight: 1.3, marginTop: '1px' }}>{amenity.description}</div>}
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '14px', padding: '8px 12px', background: '#f8f5f0', borderRadius: '6px', border: `1px solid ${BRAND.gold}40` }}>
          <span style={{ fontSize: '9px', fontWeight: '700', color: '#888', textTransform: 'uppercase', letterSpacing: '0.1em', alignSelf: 'center', marginRight: '4px' }}>Legend:</span>
          {[...new Set(nearbyAmenities.map(a => a.category))].map((cat) => {
            const config = getCategoryConfig(cat);
            return (
              <span key={cat} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '9px', fontWeight: '600', color: '#444' }}>
                <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: config.color, display: 'inline-block', flexShrink: 0 }} />
                {config.label}
              </span>
            );
          })}
        </div>

        <div style={{ height: '2px', background: `linear-gradient(90deg, ${BRAND.gold}, ${BRAND.navy}, ${BRAND.gold})`, marginBottom: '10px' }} />
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '11px', color: BRAND.red, fontWeight: '700' }}>📞</span>
            <span style={{ fontSize: '10px', color: '#555' }}>Danielle Edwards · RE/MAX Executive · 704-604-2999</span>
          </div>
          <div style={{ fontSize: '10px', color: BRAND.navy, fontWeight: '700' }}>SoldONDanielle.com</div>
        </div>
        <div style={{ height: '6px', background: BRAND.red, borderRadius: '3px', marginTop: '10px' }} />
      </div>

      <style>{`@media print { .no-print { display: none !important; } body { margin: 0; } #print-content { margin: 0; box-shadow: none; } }`}</style>
    </div>
  );
}

// ─── Main Map Component ───────────────────────────────────────────────────
export default function Home() {
  const [searchParams] = useSearchParams();
  const listingFromParams = parseListingFromParams(searchParams);

  // If no URL params, show the create modal immediately (non-dismissable)
  const hasListing = listingFromParams !== null;
  const listing = listingFromParams ?? DEFAULT_LISTING;
  // ?map=1 → clean embed mode: map only, no header/sidebar/UI
  const isEmbedMode = searchParams.get('map') === '1';

  const mapRef = useRef<HTMLDivElement>(null);
  const leafletMap = useRef<L.Map | null>(null);
  const markersRef = useRef<Map<string, L.Marker>>(new Map());
  const [selectedAmenity, setSelectedAmenity] = useState<Amenity | null>(null);
  const [activeCategories, setActiveCategories] = useState<Set<string>>(new Set(['listing', 'grocery', 'home_improvement', 'shopping', 'park', 'highway', 'hospital', 'school', 'pharmacy', 'restaurant']));
  const [legendOpen, setLegendOpen] = useState(true);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [showPrint, setShowPrint] = useState(false);
  const [mapSnapshot, setMapSnapshot] = useState('');
  const [capturing, setCapturing] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [activeTab, setActiveTab] = useState<'amenities' | 'calculator'>('amenities');
  const [showCreateListing, setShowCreateListing] = useState(false);
  const [showExportChooser, setShowExportChooser] = useState(false);
  const [showInfographic, setShowInfographic] = useState(false);
  const [showMarketingMap, setShowMarketingMap] = useState(false);
  const [nearbyAmenities, setNearbyAmenities] = useState<NearbyResult[]>([]);
  const [loadingAmenities, setLoadingAmenities] = useState(false);
  // Install prompt
  const [installPrompt, setInstallPrompt] = useState<Event | null>(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);

  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 1024);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // Init map
  useEffect(() => {
    if (!mapRef.current || leafletMap.current) return;
    const map = L.map(mapRef.current, { center: [listing.lat, listing.lng], zoom: 13, zoomControl: false });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom: 19,
    }).addTo(map);
    L.control.zoom({ position: 'bottomright' }).addTo(map);
    leafletMap.current = map;

    // Listing pin only — nearby pins added after fetch
    const lConfig = CATEGORY_CONFIG['listing'];
    const lIcon = createCustomIcon(lConfig.color, true);
    const lMarker = L.marker([listing.lat, listing.lng], { icon: lIcon, zIndexOffset: 1000 })
      .addTo(map)
      .on('click', () => { setSelectedAmenity(listing); if (isMobile) setMobileSidebarOpen(false); });
    markersRef.current.set(listing.id, lMarker);

    return () => { map.remove(); leafletMap.current = null; markersRef.current.clear(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-center map + move listing pin whenever the listing address changes
  useEffect(() => {
    if (!leafletMap.current) return;
    // Pan/zoom to new location
    leafletMap.current.setView([listing.lat, listing.lng], 13, { animate: true });
    // Move or recreate the listing pin
    const existing = markersRef.current.get(listing.id);
    if (existing) {
      existing.setLatLng([listing.lat, listing.lng]);
    } else {
      const lConfig = CATEGORY_CONFIG['listing'];
      const lIcon = createCustomIcon(lConfig.color, true);
      const lMarker = L.marker([listing.lat, listing.lng], { icon: lIcon, zIndexOffset: 1000 })
        .addTo(leafletMap.current)
        .on('click', () => { setSelectedAmenity(listing); if (isMobile) setMobileSidebarOpen(false); });
      markersRef.current.set(listing.id, lMarker);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listing.lat, listing.lng]);

  // Fetch nearby amenities when listing lat/lng changes
  // Strategy:
  //   - If this is the DEFAULT Promenade listing → show hardcoded static amenities immediately (always works)
  //   - For ANY other listing → start empty, load from Overpass only (static data belongs to a different address)
  const isDefaultListing = listing.lat === DEFAULT_LISTING.lat && listing.lng === DEFAULT_LISTING.lng;

  useEffect(() => {
    // 1. Wipe old nearby markers (keep listing pin)
    markersRef.current.forEach((marker, id) => {
      if (id !== listing.id) { marker.remove(); markersRef.current.delete(id); }
    });

    if (isDefaultListing) {
      // 2a. Default listing — load verified static amenities immediately
      const staticAsNearby: NearbyResult[] = STATIC_AMENITIES.map((a) => ({
        id: a.id,
        name: a.name,
        address: a.address,
        category: a.category,
        lat: a.lat,
        lng: a.lng,
        distanceMiles: Math.sqrt(Math.pow(a.lat - listing.lat, 2) + Math.pow(a.lng - listing.lng, 2)) * 69,
        description: a.description,
      }));
      setNearbyAmenities(staticAsNearby);

      if (leafletMap.current) {
        staticAsNearby.forEach((a) => {
          const cfg = getCategoryConfig(a.category);
          const icon = createCustomIcon(cfg.color, false);
          const m = L.marker([a.lat, a.lng], { icon })
            .addTo(leafletMap.current!)
            .on('click', () => { setSelectedAmenity(a as unknown as Amenity); if (isMobile) setMobileSidebarOpen(false); });
          markersRef.current.set(a.id, m);
        });
      }
    } else {
      // 2b. Any other listing — clear and wait for dynamic results
      setNearbyAmenities([]);
    }

    // 4. Also try Overpass — merge any NEW places it finds (don't replace static)
    setLoadingAmenities(true);
    fetchNearbyAmenities(listing.lat, listing.lng).then((dynamic) => {
      setLoadingAmenities(false);
      if (!dynamic.length) return;
      if (isDefaultListing) {
        // Merge with static — add only new places not already in the list
        setNearbyAmenities((prev) => {
          const existingIds = new Set(prev.map(a => a.id));
          const newOnes = dynamic.filter(a => !existingIds.has(a.id));
          return [...prev, ...newOnes];
        });
      } else {
        // Non-default listing — dynamic results ARE the list
        setNearbyAmenities(dynamic);
      }
      if (!leafletMap.current) return;
      dynamic.forEach((a) => {
        if (markersRef.current.has(a.id)) return;
        const cfg = getCategoryConfig(a.category);
        const icon = createCustomIcon(cfg.color, false);
        const m = L.marker([a.lat, a.lng], { icon })
          .addTo(leafletMap.current!)
          .on('click', () => { setSelectedAmenity(a as unknown as Amenity); if (isMobile) setMobileSidebarOpen(false); });
        markersRef.current.set(a.id, m);
      });
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listing.lat, listing.lng]);

  // PWA install prompt
  useEffect(() => {
    const handler = (e: Event) => { e.preventDefault(); setInstallPrompt(e); setShowInstallBanner(true); };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const installApp = async () => {
    if (!installPrompt) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (installPrompt as any).prompt();
    setShowInstallBanner(false);
    setInstallPrompt(null);
  };

  useEffect(() => { setTimeout(() => leafletMap.current?.invalidateSize(), 300); }, [mobileSidebarOpen]);

  useEffect(() => {
    if (!leafletMap.current) return;
    markersRef.current.forEach((marker, id) => {
      const cat = id === listing.id ? 'listing' : (nearbyAmenities.find(a => a.id === id)?.category ?? '');
      if (!cat) return;
      activeCategories.has(cat) ? marker.addTo(leafletMap.current!) : marker.remove();
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCategories, nearbyAmenities]);

  const toggleCategory = (cat: string) => {
    setActiveCategories((prev) => {
      const next = new Set(prev);
      if (cat === 'listing') return next;
      next.has(cat) ? next.delete(cat) : next.add(cat);
      return next;
    });
  };

  const flyToAmenity = useCallback((amenity: Amenity | NearbyResult) => {
    leafletMap.current?.flyTo([amenity.lat, amenity.lng], 15, { duration: 1.2 });
    setSelectedAmenity(amenity as Amenity);
    if (isMobile) setMobileSidebarOpen(false);
  }, [isMobile]);

  const captureAndExport = useCallback(async () => {
    if (capturing) return;
    setCapturing(true);
    if (isMobile) setMobileSidebarOpen(false);
    // Build a static map URL using multiple reliable free providers (no API key needed)
    // Provider 1: Geoapify (reliable, free tier, no key for basic)
    const { lat, lng } = listing;
    const zoom = 13;
    const w = 800, h = 500;
    // We use an OpenStreetMap tile-based URL via the reliable maps.geoapify.com service
    const staticUrl =
      `https://maps.geoapify.com/v1/staticmap?style=osm-carto&width=${w}&height=${h}` +
      `&center=lonlat:${lng},${lat}&zoom=${zoom}` +
      `&marker=lonlat:${lng},${lat};color:%23CC0000;size:large` +
      `&apiKey=YOUR_FREE_KEY`;
    // Fallback: use a simple tile-stitched URL that always works in any browser
    // We show the live map iframe instead — always correct, always loaded
    setMapSnapshot(`https://www.openstreetmap.org/export/embed.html?bbox=${lng-0.05},${lat-0.035},${lng+0.05},${lat+0.035}&layer=mapnik&marker=${lat},${lng}`);
    setShowPrint(true);
    setCapturing(false);
  }, [capturing, isMobile, listing]);

  const sidebarVisible = isMobile ? mobileSidebarOpen : true;

  // ── Embed mode: clean map-only view for iframes ──────────────────────────
  if (isEmbedMode && hasListing) {
    return (
      <div style={{ width: '100%', height: '100vh', position: 'relative' }}>
        <div ref={mapRef} style={{ position: 'absolute', inset: 0 }} />
        {/* Minimal address label */}
        <div style={{
          position: 'absolute', bottom: 40, left: '50%', transform: 'translateX(-50%)',
          background: 'rgba(0,32,96,0.9)', color: '#fff',
          fontSize: '12px', fontWeight: 700, padding: '5px 14px',
          borderRadius: '6px', pointerEvents: 'none', whiteSpace: 'nowrap',
          zIndex: 1000,
        }}>
          🏠 {listing.address}
        </div>
        {/* Branding watermark */}
        <div style={{
          position: 'absolute', bottom: 8, right: 10,
          display: 'flex', alignItems: 'center', gap: '6px',
          background: 'rgba(255,255,255,0.92)', borderRadius: '4px', padding: '3px 8px',
          zIndex: 1000,
        }}>
          <img src="/images/danielle-logo.png" style={{ height: '20px', objectFit: 'contain' }} alt="Danielle Edwards" />
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Show create modal on first open (no URL params) — non-dismissable */}
      {!hasListing && (
        <CreateListingModal
          onClose={() => {/* cannot dismiss on first open */}}
          onCreated={(url) => { window.location.href = url; }}
          forceOpen
        />
      )}

      {showExportChooser && (
        <AnimatePresence>
          <ExportChooserModal
            onClose={() => setShowExportChooser(false)}
            onPrintSheet={() => { setShowExportChooser(false); captureAndExport(); }}
            onInfographic={() => { setShowExportChooser(false); setShowInfographic(true); }}
            onMarketingMap={() => { setShowExportChooser(false); setShowMarketingMap(true); }}
          />
        </AnimatePresence>
      )}

      {showInfographic && (
        <InfographicExportModal
          listing={listing}
          amenities={nearbyAmenities}
          onClose={() => setShowInfographic(false)}
        />
      )}

      {showMarketingMap && (
        <MarketingMapExport
          listing={listing}
          amenities={nearbyAmenities}
          communityName={listing.name && listing.name !== listing.address ? listing.name : undefined}
          cityText={listing.address.split(',').slice(1, 3).join(',').trim() || undefined}
          onClose={() => setShowMarketingMap(false)}
        />
      )}

      {/* Show create modal when user clicks "New Listing" button */}
      {hasListing && showCreateListing && (
        <CreateListingModal
          onClose={() => setShowCreateListing(false)}
          onCreated={(url) => { setTimeout(() => window.open(url, '_blank'), 300); }}
        />
      )}

      {showPrint && <PrintView onClose={() => setShowPrint(false)} mapImageUrl={mapSnapshot} listing={listing} nearbyAmenities={nearbyAmenities} />}

      {/* PWA Install Banner */}
      <AnimatePresence>
        {showInstallBanner && (
          <motion.div
            initial={{ y: 80, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 80, opacity: 0 }}
            className="fixed bottom-4 left-4 right-4 z-50 flex items-center gap-3 px-4 py-3 rounded-2xl shadow-2xl"
            style={{ background: BRAND.darkNavy, border: `1px solid ${BRAND.gold}` }}
          >
            <img src="/icons/icon-72x72.png" alt="" className="w-10 h-10 rounded-xl flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-white font-bold text-sm">Install ListingMap</p>
              <p className="text-white/60 text-xs">Add to your home screen for instant access</p>
            </div>
            <button onClick={installApp} className="flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-xl text-white flex-shrink-0" style={{ background: BRAND.red }}>
              <Download className="w-3.5 h-3.5" />
              Install
            </button>
            <button onClick={() => setShowInstallBanner(false)} className="text-white/40 hover:text-white flex-shrink-0">
              <X className="w-4 h-4" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showShare && <ShareModal listing={listing} onClose={() => setShowShare(false)} />}
      </AnimatePresence>

        <div className="flex flex-col h-screen w-screen overflow-hidden font-sans" style={{ background: '#f7f4f0' }}>
        {/* ── Header ── */}
        <header className="flex-none z-20 px-3 py-2 flex items-center justify-between shadow-lg gap-2" style={{ background: BRAND.darkNavy, minHeight: '58px' }}>
          <img src="/images/danielle-logo-white.png" alt="Danielle Edwards RE/MAX Executive" className="h-9 w-auto object-contain flex-shrink-0" />

          <div className="flex-1 min-w-0 text-center px-1">
            <h1 className="font-bold text-white leading-tight text-sm md:text-base truncate" style={{ fontFamily: 'inherit' }}>
              {listing.name}
            </h1>
            <p className="text-white/60 text-xs font-mono tracking-widest hidden sm:block">South Charlotte, NC</p>
          </div>

          <div className="flex items-center gap-1.5 flex-shrink-0">
            {/* New Listing */}
            <button
              onClick={() => setShowCreateListing(true)}
              title="Create map for a new listing"
              className="hidden sm:flex items-center gap-1 text-white/80 hover:text-white text-xs font-semibold px-2.5 py-1.5 rounded-full border border-white/20 hover:border-white/50 transition-all"
              style={{ background: 'rgba(184,134,11,0.25)' }}
            >
              <Sparkles className="w-3.5 h-3.5" style={{ color: BRAND.gold }} />
              <span className="hidden md:inline">New Listing</span>
            </button>

            {/* Share */}
            <button
              onClick={() => setShowShare(true)}
              title="Share / Embed"
              className="hidden sm:flex items-center gap-1 text-white/80 hover:text-white text-xs font-semibold px-2.5 py-1.5 rounded-full border border-white/20 hover:border-white/50 transition-all"
            >
              <Share2 className="w-3.5 h-3.5" />
              <span className="hidden md:inline">Share</span>
            </button>

            {/* Export */}
            <button
              onClick={() => setShowExportChooser(true)}
              title="Export / Print"
              className="hidden sm:flex items-center gap-1 text-white/80 hover:text-white text-xs font-semibold px-2.5 py-1.5 rounded-full border border-white/20 hover:border-white/50 transition-all"
            >
              <Printer className="w-3.5 h-3.5" />
              <span className="hidden md:inline">Export</span>
            </button>

            {/* Directions */}
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${listing.lat},${listing.lng}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-white text-xs font-bold px-3 py-1.5 rounded-full hover:opacity-90 transition-opacity"
              style={{ background: BRAND.red }}
            >
              <Navigation className="w-3 h-3" />
              <span>Directions</span>
            </a>

            {/* Mobile sidebar toggle */}
            <button
              onClick={() => setMobileSidebarOpen((v) => !v)}
              className="lg:hidden flex items-center justify-center w-8 h-8 rounded-full text-white border border-white/20"
              style={{ background: mobileSidebarOpen ? BRAND.red : 'rgba(255,255,255,0.1)' }}
            >
              {mobileSidebarOpen ? <X className="w-4 h-4" /> : <List className="w-4 h-4" />}
            </button>
          </div>
        </header>

        {/* Gold accent bar */}
        <div style={{ height: '3px', background: `linear-gradient(90deg, ${BRAND.gold}, ${BRAND.red}, ${BRAND.gold})` }} />

        {/* ── Map + Sidebar ── */}
        <div className="flex flex-1 overflow-hidden relative">
          <div ref={mapRef} className="h-full z-0" style={{ flex: 1, minWidth: 0 }} />

          {/* ── Sidebar ── */}
          <AnimatePresence>
            {sidebarVisible && (
              <motion.div
                key="sidebar"
                initial={isMobile ? { x: '100%' } : false}
                animate={{ x: 0 }}
                exit={isMobile ? { x: '100%' } : { x: 0 }}
                transition={{ type: 'spring', stiffness: 350, damping: 35 }}
                className={`flex flex-col overflow-hidden z-30 ${isMobile ? 'absolute right-0 top-0 bottom-0 w-4/5 max-w-xs shadow-2xl' : 'relative w-72 border-l'}`}
                style={{ background: 'rgba(255,252,248,0.97)', borderColor: '#e2d9cc' }}
              >
                {/* Sidebar brand header */}
                <div className="px-4 py-2.5 flex items-center justify-between border-b" style={{ background: BRAND.darkNavy, borderColor: BRAND.navy }}>
                  <div>
                    <p className="text-xs font-semibold text-white">Neighborhood Map</p>
                    <p className="text-white/50 text-xs font-mono">SoldONDanielle.com</p>
                  </div>
                  <img src="/images/remax-white.png" alt="RE/MAX Executive" className="h-7 w-auto object-contain opacity-90" />
                </div>

                {/* ── Tabs ── */}
                <div className="flex border-b" style={{ borderColor: '#e2d9cc' }}>
                  <button
                    onClick={() => setActiveTab('amenities')}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-bold border-b-2 transition-colors"
                    style={activeTab === 'amenities'
                      ? { borderBottomColor: BRAND.darkNavy, color: BRAND.darkNavy, background: 'white' }
                      : { borderBottomColor: 'transparent', color: '#888', background: '#f0ebe3' }}
                  >
                    <MapPin className="w-3.5 h-3.5" />
                    Amenities
                  </button>
                  <button
                    onClick={() => setActiveTab('calculator')}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-bold border-b-2 transition-colors"
                    style={activeTab === 'calculator'
                      ? { borderBottomColor: BRAND.red, color: BRAND.red, background: 'white' }
                      : { borderBottomColor: 'transparent', color: '#888', background: '#f0ebe3' }}
                  >
                    <Calculator className="w-3.5 h-3.5" />
                    Showing Cost
                  </button>
                </div>

                {/* ── Amenities tab ── */}
                {activeTab === 'amenities' && (
                  <>
                    <button
                      onClick={() => setLegendOpen((v) => !v)}
                      className="flex items-center justify-between px-4 py-2.5 border-b hover:opacity-90 transition-opacity"
                      style={{ background: '#f0ebe3', borderColor: '#e2d9cc' }}
                    >
                      <span className="text-xs font-semibold" style={{ color: BRAND.darkNavy }}>Filter by Category</span>
                      {legendOpen ? <ChevronUp className="w-3.5 h-3.5" style={{ color: BRAND.navy }} /> : <ChevronDown className="w-3.5 h-3.5" style={{ color: BRAND.navy }} />}
                    </button>

                    <AnimatePresence>
                      {legendOpen && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }}
                          className="overflow-hidden"
                        >
                          <div className="p-3 flex flex-wrap gap-1.5 border-b" style={{ borderColor: '#e2d9cc' }}>
                            {[...new Set([listing, ...nearbyAmenities].map(a => a.category))].filter(c => c !== 'listing').map((cat) => {
                              const config = getCategoryConfig(cat);
                              const active = activeCategories.has(cat);
                              return (
                                <button
                                  key={cat}
                                  onClick={() => toggleCategory(cat)}
                                  className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold border transition-all duration-150"
                                  style={active
                                    ? { background: config.color, color: 'white', borderColor: config.color }
                                    : { background: 'transparent', color: '#888', borderColor: '#ccc' }}
                                >
                                  <span>{config.emoji}</span>{config.label}
                                </button>
                              );
                            })}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    <div className="flex-1 overflow-y-auto">
                      {loadingAmenities && (
                        <div className="flex items-center gap-2 px-4 py-3 text-xs text-gray-400">
                          <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                          Finding nearby places…
                        </div>
                      )}
                      {[listing, ...nearbyAmenities
                        .filter((a) => !a.distanceMiles || a.distanceMiles <= 6)
                        .filter((a, i, arr) => arr.findIndex(b => b.name === a.name) === i)
                      ].filter((a) => activeCategories.has(a.category)).map((amenity) => {
                        const config = getCategoryConfig(amenity.category);
                        const isSelected = selectedAmenity?.id === amenity.id;
                        return (
                          <motion.button
                            key={amenity.id}
                            whileHover={{ x: 3 }}
                            transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                            onClick={() => flyToAmenity(amenity)}
                            className="w-full text-left px-4 py-3 border-b flex items-start gap-3 transition-colors"
                            style={{ borderColor: '#e2d9cc', background: isSelected ? '#f0e8d8' : 'transparent' }}
                          >
                            <div className="mt-0.5 w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-sm" style={{ background: config.color }}>
                              <span className="leading-none">{config.emoji}</span>
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-bold leading-snug truncate" style={{ color: BRAND.darkNavy, fontFamily: 'inherit' }}>{amenity.name}</p>
                              <p className="text-xs mt-0.5 leading-snug font-mono" style={{ color: '#888' }}>{amenity.address}</p>
                              {amenity.description && <p className="text-xs leading-snug mt-0.5" style={{ color: '#aaa' }}>{amenity.description}</p>}
                            </div>
                          </motion.button>
                        );
                      })}
                    </div>
                  </>
                )}

                {/* ── Calculator tab ── */}
                {activeTab === 'calculator' && <ShowingCalculator listing={listing} />}

                {/* Sidebar footer */}
                <div className="px-4 py-2 border-t flex items-center justify-between" style={{ borderColor: '#e2d9cc', background: '#f0ebe3' }}>
                  <div className="flex items-center gap-1.5">
                    <Phone className="w-3 h-3" style={{ color: BRAND.red }} />
                    <span className="text-xs font-semibold" style={{ color: BRAND.darkNavy }}>Danielle · 704-604-2999</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => setShowShare(true)} className="p-1.5 rounded-full hover:bg-white/50 transition-colors" title="Share">
                      <Share2 className="w-3.5 h-3.5" style={{ color: BRAND.navy }} />
                    </button>
                    <button onClick={captureAndExport} disabled={capturing} className="p-1.5 rounded-full hover:bg-white/50 transition-colors disabled:opacity-50" title="Export">
                      {capturing ? <Loader className="w-3.5 h-3.5 animate-spin" style={{ color: BRAND.navy }} /> : <Printer className="w-3.5 h-3.5" style={{ color: BRAND.navy }} />}
                    </button>
                    <button onClick={() => setShowShare(true)} className="p-1.5 rounded-full hover:bg-white/50 transition-colors" title="Embed">
                      <Code2 className="w-3.5 h-3.5" style={{ color: BRAND.navy }} />
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Mobile backdrop */}
          {isMobile && mobileSidebarOpen && (
            <div className="absolute inset-0 z-20 bg-black/40 lg:hidden" onClick={() => setMobileSidebarOpen(false)} />
          )}

          {/* Selected amenity popup */}
          <AnimatePresence>
            {selectedAmenity && !mobileSidebarOpen && (
              <motion.div
                key={selectedAmenity.id}
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }}
                transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                className={`absolute bottom-4 left-4 z-20 rounded-2xl shadow-2xl p-4 flex items-start gap-3 border ${isMobile ? 'right-4' : 'right-[295px]'}`}
                style={{ background: 'white', borderColor: '#e2d9cc' }}
              >
                <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 text-lg" style={{ background: CATEGORY_CONFIG[selectedAmenity.category].color }}>
                  {CATEGORY_CONFIG[selectedAmenity.category].emoji}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm leading-snug" style={{ color: BRAND.darkNavy, fontFamily: 'inherit' }}>{selectedAmenity.name}</p>
                  <p className="text-xs font-mono mt-0.5" style={{ color: '#888' }}>{selectedAmenity.address}</p>
                  {selectedAmenity.description && <p className="text-xs mt-1" style={{ color: '#aaa' }}>{selectedAmenity.description}</p>}
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(selectedAmenity.name + ' ' + selectedAmenity.address)}`}
                    target="_blank" rel="noopener noreferrer"
                    className="mt-2 inline-flex items-center gap-1 text-xs font-bold hover:underline"
                    style={{ color: BRAND.red }}
                  >
                    <Navigation className="w-3 h-3" />
                    Open in Google Maps
                  </a>
                </div>
                <button onClick={() => setSelectedAmenity(null)} className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0">
                  <X className="w-4 h-4" />
                </button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Mobile FABs */}
          {isMobile && !mobileSidebarOpen && (
            <div className="absolute bottom-4 right-4 z-10 flex flex-col gap-2">
              <button onClick={() => setShowShare(true)} className="w-10 h-10 rounded-full shadow-lg flex items-center justify-center" style={{ background: BRAND.navy }} title="Share">
                <Share2 className="w-4 h-4 text-white" />
              </button>
              <button onClick={captureAndExport} disabled={capturing} className="w-10 h-10 rounded-full shadow-lg flex items-center justify-center disabled:opacity-50" style={{ background: BRAND.darkNavy }} title="Export">
                {capturing ? <Loader className="w-4 h-4 text-white animate-spin" /> : <Printer className="w-4 h-4 text-white" />}
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
